<template>
  <div class="min-h-screen flex items-center justify-center p-6">
    <div class="max-w-lg w-full p-6 rounded-xl shadow-lg bg-[var(--color-surface)] border border-[var(--color-border)]">
      <h2 class="text-xl font-semibold mb-1 text-[color:var(--color-text)]">{{ $t('tenantSelection.title') }}</h2>
      <p class="text-sm mb-5 text-[color:var(--color-muted)]">{{ $t('tenantSelection.subtitle') }}</p>

      <div v-if="loading" class="state-empty">{{ $t('tenantSelection.loading') }}</div>

      <div v-else-if="tenants.length === 0" class="state-empty">{{ $t('tenantSelection.noTenants') }}</div>

      <div v-else class="space-y-3">
        <div
          v-for="t in tenants"
          :key="t.id ?? t.slug"
          class="data-card"
        >
          <div class="data-card__main">
            <div class="data-card__name">{{ t.name }}</div>
            <div class="data-card__meta">
              <span class="badge badge-muted">{{ t.slug }}</span>
              <span v-if="t.has_session === false" class="badge badge-warning">{{ $t('tenantSelection.stepUpRequired') }}</span>
            </div>
          </div>
          <div class="data-card__actions">
            <button class="btn primary small" :disabled="processing" @click="selectTenant(t)">
              {{ $t('tenantSelection.select') }}
            </button>
          </div>
        </div>
      </div>

      <div v-if="error" class="text-sm text-red-400 mt-4">{{ error }}</div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth.js'
import { useStore } from '@/stores/store.js'
import { getTenants } from '@/api/api.js'

const router = useRouter()
const authStore = useAuthStore()
const store = useStore()
const { t } = useI18n()

const loading = ref(true)
const processing = ref(false)
const error = ref(null)
const tenants = ref([])

onMounted(async () => {
  // Use available_tenants from the auth store if already populated
  if (authStore.availableTenants.length) {
    tenants.value = authStore.availableTenants
    loading.value = false
    return
  }
  // Otherwise fetch from the API
  try {
    const res = await getTenants()
    tenants.value = res.results ?? res.tenants ?? res ?? []
  } catch (e) {
    error.value = t('tenantSelection.loadFailed')
  } finally {
    loading.value = false
  }
})

async function selectTenant(t) {
  processing.value = true
  error.value = null
  try {
    await store.switchTenant(t.slug)
    router.replace('/')
  } catch (e) {
    // step_up_required is handled by the global event → App.vue → /auth/step-up
    if (!e.data?.step_up_required) {
      error.value = e.data?.error || e.data?.detail || t('tenantSelection.selectFailed')
    }
  } finally {
    processing.value = false
  }
}
</script>
